// ============================================
// RESUMENEMPRESA.JS — la tercera vista de Expedientes (EXPEDIENTES-RESUMEN, 2026-09-17)
// ============================================
// La foto completa del negocio, en tres bloques: EL TRABAJO (cuanto hay y cuanto esta atorado),
// EL DINERO (que hay de verdad en juego) y ESTE MES (lo que se cerro). Cada renglon es un boton
// que lleva a la lista ya filtrada: el resumen no es un cuadro para mirar, es la puerta de entrada.
//
// SOLO DIRECCION. La opcion ni siquiera se dibuja para los demas, y el endpoint que alimenta esta
// pantalla responde 403 a quien no deba verlo (src/routes/resumenExpedientes.js). El candado real
// es el del servidor; esto es solo la pantalla.
//
// SIEMPRE ES EL TOTAL DE LA EMPRESA, este el usuario en la pestaña que este: un resumen del
// "Archivo" no significaria nada. Por eso, mientras el Resumen esta abierto, las pestañas y los
// filtros de la lista se esconden (no le aplican) y vuelven al cambiar a Tabla o Kanban.
//
// EL DINERO, DICHO COMO ES
// La cifra vieja de portada ("Valor en curso", $9,845,992) metia en el mismo saco cotizaciones
// AUTORIZADAS, enviadas, BORRADORES a medias y hasta RECHAZADAS. El 93% de esa cifra eran
// borradores que el cliente nunca vio. Aqui se separa: lo que esta VIVO (autorizado + enviado) va
// arriba y en grande; lo que NO esta en juego (borradores y rechazadas) se enseña abajo, aparte y
// con su nombre. Las cifras salen del backend, que a su vez LEE `cotizaciones.total` — el total ya
// calculado por el resolutor de precios del proyecto. Aqui no se multiplica ni se suma nada.

import React from 'react';
import { ChevronRight, Folder, AlertTriangle, Clock, FileQuestion, UserX, Wallet, TrendingUp, Archive } from 'lucide-react';
import { formatMoneda } from '../../utils/estiloExpedientes';

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const COLOR_OSCURO  = '#270f2b';
const COLOR_GRIS    = '#5e666e';
const COLOR_GRIS_CL = '#9aa0a6';
const COLOR_BORDE   = '#e5e7eb';
const COLOR_AZUL    = '#185FA5';

const TONOS = {
  verde: '#16a34a', azul: '#2f5f9e', rojo: '#dc2626', gris: '#9aa0a6', ambar: '#d97706',
};

// ---------- Un renglon clicable del resumen ----------
function Renglon({ Icono, label, ayuda, valor, sub, color, onClick, apagado, esCelular }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={ayuda}
      className="beep-renglon-resumen"
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: esCelular ? '11px 10px' : '11px 12px',
        background: 'transparent', border: 'none', borderRadius: 9,
        borderBottom: `1px solid ${COLOR_BORDE}`,
        textAlign: 'left', fontFamily: FF, minHeight: 48,
        cursor: onClick ? 'pointer' : 'default',
        opacity: apagado ? 0.62 : 1,
      }}
    >
      {Icono && (
        <span style={{ flexShrink: 0, display: 'grid', placeItems: 'center', width: 26, height: 26, color: color || COLOR_GRIS_CL }}>
          <Icono size={17} strokeWidth={2.1} />
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontSize: esCelular ? 13 : 13.5, fontWeight: 600, color: COLOR_OSCURO,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{label}</span>
        {sub && (
          <span style={{
            display: 'block', marginTop: 1, fontSize: 11.5, color: COLOR_GRIS_CL, lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{sub}</span>
        )}
      </span>
      <span style={{
        flexShrink: 0, fontSize: esCelular ? 15 : 16, fontWeight: 800, color: color || COLOR_OSCURO,
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      }}>{valor}</span>
      {onClick && <ChevronRight size={15} strokeWidth={2.4} color={COLOR_GRIS_CL} style={{ flexShrink: 0 }} />}
    </button>
  );
}

// ---------- Un bloque (tarjeta) ----------
function Bloque({ titulo, pie, cabecera, children }) {
  return (
    <section style={{
      background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 14,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', minWidth: 0,
    }}>
      <header style={{ padding: '13px 14px 10px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
        <h2 style={{
          margin: 0, fontFamily: FF, fontSize: 12, fontWeight: 800, letterSpacing: .7,
          textTransform: 'uppercase', color: COLOR_GRIS_CL,
        }}>{titulo}</h2>
        {cabecera}
      </header>
      <div style={{ padding: '2px 4px 4px' }}>{children}</div>
      {pie && (
        <footer style={{
          marginTop: 'auto', padding: '10px 14px', background: '#fafbfc',
          borderTop: `1px solid ${COLOR_BORDE}`, fontSize: 11.5, color: COLOR_GRIS, lineHeight: 1.45,
        }}>{pie}</footer>
      )}
    </section>
  );
}

// Cifra grande de cabecera de bloque.
function CifraGrande({ valor, etiqueta, color, esCelular }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{
        fontFamily: FF, fontSize: esCelular ? 26 : 30, fontWeight: 800, lineHeight: 1.05,
        color: color || COLOR_OSCURO, fontVariantNumeric: 'tabular-nums',
      }}>{valor}</div>
      <div style={{ marginTop: 2, fontSize: 12, color: COLOR_GRIS }}>{etiqueta}</div>
    </div>
  );
}

export default function ResumenEmpresa({ datos, cargando, error, onReintentar, onIrALista, esCelular }) {
  if (error) {
    return (
      <div style={{
        background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
        padding: 18, borderRadius: 12, fontSize: 13, fontFamily: FF,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      }}>
        <span>No se pudo cargar el resumen: {error}</span>
        {onReintentar && (
          <button onClick={onReintentar} style={{
            background: '#fff', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8,
            padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
          }}>Reintentar</button>
        )}
      </div>
    );
  }

  if (cargando || !datos) {
    return (
      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: esCelular ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {[0, 1, 2].map(i => <div key={i} className="beep-skel" style={{ height: 250, borderRadius: 14 }} />)}
      </div>
    );
  }

  const t = datos.trabajo || {};
  const d = datos.dinero || {};
  const c = datos.cierre || {};
  const renglones = d.renglones || [];
  const vivos = renglones.filter(r => r.vivo);
  const fuera = renglones.filter(r => !r.vivo);
  const ir = (filtro) => (onIrALista ? () => onIrALista(filtro) : undefined);

  return (
    <div style={{ fontFamily: FF }}>
      <style>{`
        .beep-renglon-resumen:hover:not(:disabled) { background: #f5f8fb; }
        .beep-renglon-resumen:last-child { border-bottom: none; }
        .beep-renglon-resumen:active:not(:disabled) { transform: scale(.995); }
      `}</style>

      <div style={{
        display: 'grid', gap: 14,
        gridTemplateColumns: esCelular ? '1fr' : 'repeat(auto-fit, minmax(310px, 1fr))',
        alignItems: 'start',
      }}>

        {/* ============ BLOQUE 1 — EL TRABAJO ============ */}
        <Bloque
          titulo="El trabajo"
          cabecera={<CifraGrande valor={t.activos} etiqueta="expedientes abiertos ahora mismo" esCelular={esCelular} />}
          pie={`Un expediente esta "atorado" cuando lleva mas de ${datos.umbrales?.rojo ?? 7} dias sin moverse de paso.`}
        >
          <Renglon
            Icono={AlertTriangle} label="Atorados" color={TONOS.rojo} valor={t.atorados}
            sub={`mas de ${datos.umbrales?.rojo ?? 7} dias sin moverse`}
            ayuda="Llevan mas dias de los debidos en el mismo paso"
            onClick={ir({ pestana: 'activos', semaforoFiltro: 'rojo' })} esCelular={esCelular}
          />
          <Renglon
            Icono={Clock} label="Tardando" color={TONOS.ambar} valor={t.tardando}
            sub={`entre ${datos.umbrales?.amarillo ?? 3} y ${datos.umbrales?.rojo ?? 7} dias`}
            onClick={ir({ pestana: 'activos', semaforoFiltro: 'amarillo' })} esCelular={esCelular}
          />
          <Renglon
            Icono={FileQuestion} label="Sin cotizar" color={TONOS.azul} valor={t.sin_cotizar}
            sub="sin cotizacion todavia"
            ayuda="El dinero olvidado: expedientes abiertos a los que nadie les ha puesto precio"
            onClick={ir({ pestana: 'activos', sinCotizarFiltro: 'si' })} esCelular={esCelular}
          />
          <Renglon
            Icono={UserX} label="Sin responsable" color={t.sin_responsable > 0 ? TONOS.ambar : TONOS.gris}
            valor={t.sin_responsable} sub="nadie asignado"
            onClick={ir({ pestana: 'activos', responsableId: 'sin' })} esCelular={esCelular}
          />
          <Renglon
            Icono={Folder} label="Ver todos los activos" valor={t.activos} color={COLOR_AZUL}
            onClick={ir({ pestana: 'activos' })} esCelular={esCelular}
          />
        </Bloque>

        {/* ============ BLOQUE 2 — EL DINERO ============ */}
        <Bloque
          titulo="El dinero en juego"
          cabecera={
            <>
              <CifraGrande valor={formatMoneda(d.vivo)} etiqueta="autorizado + enviado al cliente" color={TONOS.verde} esCelular={esCelular} />
              <div style={{ marginTop: 4, fontSize: 11.5, color: COLOR_GRIS_CL }}>cifras con IVA</div>
            </>
          }
          pie={
            <>
              <strong style={{ color: COLOR_OSCURO }}>De donde sale:</strong> de cada expediente abierto se toma su
              cotizacion autorizada; si no tiene ninguna autorizada, la ultima capturada. Se suma el TOTAL de esa
              cotizacion (con IVA), tal cual quedo guardado. Nada se recalcula aqui.
            </>
          }
        >
          {vivos.map(r => (
            <Renglon
              key={r.id} Icono={r.id === 'autorizada' ? TrendingUp : Wallet}
              label={r.label} ayuda={r.ayuda} color={TONOS[r.tono] || COLOR_OSCURO}
              valor={formatMoneda(r.total)} sub={`${r.expedientes} ${r.expedientes === 1 ? 'expediente' : 'expedientes'}`}
              onClick={ir({ pestana: 'activos', cotizacionFiltro: r.id })} esCelular={esCelular}
            />
          ))}
          <div style={{
            margin: '8px 10px 4px', padding: '7px 10px', borderRadius: 8, background: '#f7f8f9',
            fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CL, letterSpacing: .3, textTransform: 'uppercase',
          }}>
            No esta en juego
          </div>
          {fuera.map(r => (
            <Renglon
              key={r.id} Icono={Wallet} apagado
              label={r.label} ayuda={r.ayuda} color={TONOS[r.tono] || COLOR_GRIS_CL}
              valor={formatMoneda(r.total)} sub={`${r.expedientes} ${r.expedientes === 1 ? 'expediente' : 'expedientes'}`}
              onClick={ir({ pestana: 'activos', cotizacionFiltro: r.id })} esCelular={esCelular}
            />
          ))}
          <Renglon
            Icono={FileQuestion} apagado label="Sin ninguna cotizacion" color={COLOR_GRIS_CL}
            valor={d.sin_cotizar} sub="no hay cifra que sumar todavia"
            onClick={ir({ pestana: 'activos', sinCotizarFiltro: 'si' })} esCelular={esCelular}
          />
        </Bloque>

        {/* ============ BLOQUE 3 — ESTE MES ============ */}
        <Bloque
          titulo="Cerrado este mes"
          cabecera={<CifraGrande valor={formatMoneda(c.ganados?.total || 0)} etiqueta={`ganado en ${(c.ganados?.n || 0)} ${(c.ganados?.n === 1 ? 'expediente' : 'expedientes')}`} color={TONOS.verde} esCelular={esCelular} />}
          pie="El mes va del dia 1 al de hoy, con el reloj de Ciudad de Mexico."
        >
          <Renglon
            Icono={TrendingUp} label="Ganados" color={TONOS.verde}
            valor={c.ganados?.n || 0} sub={formatMoneda(c.ganados?.total || 0)}
            onClick={ir({ pestana: 'archivo', resultadoFiltro: 'ganado', cierreMes: true })} esCelular={esCelular}
          />
          <Renglon
            Icono={Archive} label="Perdidos" color={(c.perdidos?.n || 0) > 0 ? TONOS.rojo : TONOS.gris}
            valor={c.perdidos?.n || 0} sub={formatMoneda(c.perdidos?.total || 0)}
            onClick={ir({ pestana: 'archivo', resultadoFiltro: 'perdido', cierreMes: true })} esCelular={esCelular}
          />
          <Renglon
            Icono={Archive} label="Descartados" color={TONOS.gris}
            valor={c.descartados?.n || 0} sub="duplicados o errores"
            onClick={ir({ pestana: 'archivo', resultadoFiltro: 'descartado', cierreMes: true })} esCelular={esCelular}
          />
          <Renglon
            Icono={Folder} label="Ver todo el archivo" valor="" color={COLOR_AZUL}
            onClick={ir({ pestana: 'archivo' })} esCelular={esCelular}
          />
        </Bloque>
      </div>
    </div>
  );
}
